import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer, X, Loader2 } from "lucide-react";
import { DeepAnalysisReport, combinedVerdict } from "./DeepAnalysis";
import { BisnisReport, IklanReport } from "../components/potensiReports";
import { PrintReadyProvider } from "../lib/printReady";
import { readPrintTicket } from "../lib/printTicket";
import { recordPdfExport } from "../lib/api";
import { idLabel } from "../lib/idLabels";

// The Deep Analysis PDF, rendered by the browser's print engine from the SAME
// React components as the on-screen report, so the two cannot drift apart.
// Page 1 is a cover laid out in millimetres to match the original jsPDF cover;
// everything after it is <DeepAnalysisReport printMode />. The route opens from
// "Unduh Laporan PDF" with a ticket (lib/printTicket.js), shows the sheet as a
// preview, and opens the print dialog once the maps have taken their snapshots.

const PT = 0.3528;   // 1pt in mm

// Cover colours — the jsPDF palette, kept so the cover reads as before.
const GOLD = "#c9a25a", MUTE = "#94a3b8", TRACK = "#263438", DIVIDER = "#3a494c";
const GREEN = "#16a34a", AMBER = "#d97706", RED = "#dc2626";

const fitLabel = (v) => (v >= 8 ? "Sangat cocok" : v >= 5 ? "Cukup cocok" : "Kurang cocok");
const fitColor = (v) => (v >= 8 ? GREEN : v >= 5 ? AMBER : RED);

// Text placed by baseline, like pdf.text(): top ≈ baseline − cap height.
// `x` is the left edge, the right edge, or the centre, depending on `align`.
function T({ x = 105, baseline, pt, color, weight = 400, spacing = 0, align = "left", children }) {
  const style = {
    fontSize: `${pt * PT}mm`, color, fontWeight: weight, letterSpacing: spacing ? `${spacing}mm` : undefined,
    top: `${baseline - pt * PT * 0.72}mm`,
  };
  if (align === "center") { style.left = `${x}mm`; style.transform = "translateX(-50%)"; }
  else if (align === "right") { style.right = `${210 - x}mm`; }
  else style.left = `${x}mm`;
  return <div className="abs" style={style}>{children}</div>;
}

function Rule({ y, color, width = 0.3, x1 = 16, x2 = 194 }) {
  return <div className="abs" style={{ left: `${x1}mm`, width: `${x2 - x1}mm`, top: `${y}mm`, height: `${width}mm`, background: color }} />;
}

// Score ring: r = 25mm, stroke 4.2mm, drawn from 12 o'clock like the jsPDF arc.
function Ring({ cx, cy, value, max, color, topLabel, unit, subLabel, subColor }) {
  const r = 25, sw = 4.2, box = 2 * (r + sw);
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(max, value)) / max;
  return (
    <>
      <svg className="abs" style={{ left: `${cx - box / 2}mm`, top: `${cy - box / 2}mm`, width: `${box}mm`, height: `${box}mm` }}
        viewBox={`0 0 ${box} ${box}`}>
        <circle cx={box / 2} cy={box / 2} r={r} fill="none" stroke={TRACK} strokeWidth={sw} />
        {pct > 0 && (
          <circle cx={box / 2} cy={box / 2} r={r} fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round"
            strokeDasharray={`${circ * pct} ${circ}`} transform={`rotate(-90 ${box / 2} ${box / 2})`} />
        )}
      </svg>
      <T x={cx} baseline={cy - r - 7} pt={7.5} color={GOLD} weight={700} spacing={2} align="center">{topLabel}</T>
      <T x={cx} baseline={cy + 2} pt={29} color="#fff" weight={800} align="center">{value}</T>
      <T x={cx} baseline={cy + 9} pt={7} color={MUTE} spacing={1.2} align="center">{unit}</T>
      <T x={cx} baseline={cy + r + 11} pt={10} color={subColor} weight={700} align="center">{subLabel}</T>
    </>
  );
}

// A Potensi report's cover: same frame, one ring, its own kicker and meta strip.
function PotensiCover({ view }) {
  const { gridLabel, fullLocation, cover = {} } = view;
  const ring = cover.ring || {};
  const cy = 158, metaY = 247;
  const genDate = new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  return (
    <div className="print-cover">
      <Rule y={20} color={GOLD} width={0.4} />
      <T x={16} baseline={31} pt={22} color="#fff" weight={700} spacing={1.2}>LANDHEP</T>
      <T x={16} baseline={36.5} pt={7.5} color={GOLD} spacing={2.4}>INTELIJEN LOKASI</T>
      <T x={194} baseline={31} pt={7.5} color={MUTE} align="right">info@landhep.id</T>
      <T x={194} baseline={36.5} pt={7.5} color={MUTE} align="right">+62 851-1121-4177</T>
      <T baseline={92} pt={9} color={GOLD} weight={700} spacing={3} align="center">{cover.kicker || "LAPORAN LANDHEP"}</T>
      <T baseline={106} pt={26} color="#fff" weight={700} align="center">{gridLabel ?? "Laporan Lokasi"}</T>
      <div className="abs wrap" style={{ left: "24mm", right: "24mm", top: `${115 - 10 * PT * 0.72}mm`, textAlign: "center",
        fontSize: `${10 * PT}mm`, lineHeight: "5.5mm", color: MUTE, maxHeight: "11mm", overflow: "hidden" }}>
        {fullLocation || "-"}
      </div>
      {ring.value != null && (
        <Ring cx={105} cy={cy} value={ring.value} max={ring.max || 100} color={ring.color || GREEN}
          topLabel={ring.top || ""} unit={ring.unit || "DARI 100"} subLabel={ring.sub || ""} subColor={ring.color || GREEN} />
      )}
      <Rule y={metaY - 9} color={GOLD} />
      {(cover.metas || []).slice(0, 3).map(([label, val], i) => (
        <div key={label}>
          <T x={16 + i * 62} baseline={metaY} pt={6.8} color={MUTE} spacing={1.3}>{String(label).toUpperCase()}</T>
          <div className="abs" style={{ left: `${16 + i * 62}mm`, top: `${metaY + 6 - 9.5 * PT * 0.72}mm`, width: "52mm", fontSize: `${9.5 * PT}mm`,
            fontWeight: 700, color: "#ecf0f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{String(val ?? "-")}</div>
        </div>
      ))}
      <T x={16} baseline={279} pt={8} color={MUTE}>Dibuat {genDate}</T>
    </div>
  );
}

function PrintCover({ view }) {
  const { gridLabel, fullLocation, namaKabkot, score = 0, idx = 0, yourFit, verdict, subcategory, targetSes, radius,
    groundLabel, suitabilityLabel: suitability = "-", generatedLabel } = view;
  const sColor = idx < 2 ? RED : idx === 2 ? AMBER : GREEN;
  const genDate = generatedLabel
    || new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  const cy = 158, r = 25;
  const cv = combinedVerdict(verdict, groundLabel);
  const vColor = cv?.tone === "good" ? GREEN : cv?.tone === "bad" ? RED : AMBER;
  const metaY = 247;   // H - 50

  return (
    <div className="print-cover">
      <Rule y={20} color={GOLD} width={0.4} />
      <T x={16} baseline={31} pt={22} color="#fff" weight={700} spacing={1.2}>LANDHEP</T>
      <T x={16} baseline={36.5} pt={7.5} color={GOLD} spacing={2.4}>INTELIJEN LOKASI</T>
      <T x={194} baseline={31} pt={7.5} color={MUTE} align="right">info@landhep.id</T>
      <T x={194} baseline={36.5} pt={7.5} color={MUTE} align="right">+62 851-1121-4177</T>

      <T baseline={92} pt={9} color={GOLD} weight={700} spacing={3} align="center">LAPORAN ANALISIS MENDALAM</T>
      <T baseline={106} pt={26} color="#fff" weight={700} align="center">{gridLabel ?? "Laporan Lokasi"}</T>
      <div className="abs wrap" style={{ left: "24mm", right: "24mm", top: `${115 - 10 * PT * 0.72}mm`, textAlign: "center",
        fontSize: `${10 * PT}mm`, lineHeight: "5.5mm", color: MUTE, maxHeight: "11mm", overflow: "hidden" }}>
        {fullLocation || namaKabkot || "-"}
      </div>

      {yourFit != null ? (
        <>
          <Ring cx={105 - 37} cy={cy} value={score} max={100} color={sColor}
            topLabel="SKOR LOKASI" unit="DARI 100" subLabel={`Kesesuaian ${suitability}`} subColor={sColor} />
          <Ring cx={105 + 37} cy={cy} value={yourFit} max={10} color={fitColor(yourFit)}
            topLabel="KECOCOKAN ANDA" unit="DARI 10" subLabel={fitLabel(yourFit)} subColor={fitColor(yourFit)} />
          <div className="abs" style={{ left: "105mm", top: `${cy - r + 3}mm`, height: `${2 * r - 6}mm`, width: "0.3mm", background: DIVIDER }} />
        </>
      ) : (
        <Ring cx={105} cy={cy} value={score} max={100} color={sColor}
          topLabel="SKOR LOKASI" unit="DARI 100" subLabel={`Kesesuaian ${suitability}`} subColor={sColor} />
      )}

      {verdict && (
        <div className="abs" style={{ left: 0, right: 0, top: `${cy + r + 18}mm`, textAlign: "center" }}>
          <span style={{ display: "inline-block", height: "9mm", lineHeight: "9mm", padding: "0 8mm", borderRadius: "4.5mm",
            background: vColor, color: "#fff", fontSize: `${9 * PT}mm`, fontWeight: 700 }}>
            {verdict}{cv?.suffix && <span style={{ fontWeight: 500, opacity: 0.9 }}> · {cv.suffix}</span>}
          </span>
        </div>
      )}

      <Rule y={metaY - 9} color={GOLD} />
      {[["Bisnis", subcategory || "-", 16], ["Target SES", idLabel(String(targetSes || "all")), 78], ["Radius Pemindaian", radius || "-", 128]].map(([label, val, x]) => (
        <div key={label}>
          <T x={x} baseline={metaY} pt={6.8} color={MUTE} spacing={1.3}>{label.toUpperCase()}</T>
          <div className="abs" style={{ left: `${x}mm`, top: `${metaY + 6 - 9.5 * PT * 0.72}mm`, width: "52mm", fontSize: `${9.5 * PT}mm`,
            fontWeight: 700, color: "#ecf0f0", overflow: "hidden", textOverflow: "ellipsis" }}>{val}</div>
        </div>
      ))}
      <T x={16} baseline={279} pt={8} color={MUTE}>Dibuat {genDate}</T>
    </div>
  );
}

export default function DeepAnalysisPrint() {
  const [params] = useSearchParams();
  const ticket = useMemo(() => readPrintTicket(params.get("k")), []);   // eslint-disable-line react-hooks/exhaustive-deps
  const view = ticket?.view || {};
  const [ready, setReady] = useState(false);
  // The sheet renders only once print.css is in: before that the page is screen
  // width, and a map that initialises at 1500 px takes its snapshot at that size —
  // then gets cropped to the 210 mm sheet, markers and all.
  const [cssReady, setCssReady] = useState(false);
  const recorded = useRef(false);

  // The route has no app chrome and must scroll; the app shell locks overflow.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-print", "1");
    const link = document.createElement("link");
    link.rel = "stylesheet"; link.href = "/print.css";
    link.onload = () => setCssReady(true);
    link.onerror = () => setCssReady(true);   // render anyway rather than hang
    document.head.appendChild(link);
    return () => { root.removeAttribute("data-print"); link.remove(); root.style.removeProperty("--print-title"); };
  }, []);

  // Chrome names the PDF after the tab title; the running header reads --print-title.
  useEffect(() => {
    if (!ticket) return;
    document.title = ticket.fileName || "Landhep_DeepAnalysis";
    const kind = ticket.kind === "potensi"
      ? (ticket.type === "potensi_iklan" ? "Laporan Potensi Iklan" : "Laporan Potensi Bisnis")
      : "Laporan Analisis Mendalam";
    document.documentElement.style.setProperty("--print-title", JSON.stringify(`${kind} · ${view.gridLabel || ""}`));
  }, [ticket, view.gridLabel]);

  const print = useCallback(async () => {
    if (!ticket) return;
    try { await document.fonts?.ready; } catch { /* print anyway */ }
    // Audit once per opened sheet — the same log the jsPDF export wrote.
    if (!recorded.current && ticket.audit?.gridId) {
      recorded.current = true;
      recordPdfExport(ticket.audit.gridId, ticket.audit.meta);
    }
    window.print();
  }, [ticket]);

  // `?autoprint=0` keeps the dialog closed (QA: inspect the sheet without Chrome's
  // modal print preview blocking the tab); the toolbar button still prints.
  const autoPrint = params.get("autoprint") !== "0";
  const onReady = useCallback(() => { setReady(true); if (autoPrint) print(); }, [print, autoPrint]);

  const isPotensi = ticket?.kind === "potensi" && ticket?.report;
  if (!isPotensi && !ticket?.result?.analysis) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 px-6 text-center bg-slate-50">
        <p className="text-sm font-bold text-slate-800">Tidak ada laporan untuk dicetak</p>
        <p className="text-xs text-slate-500 max-w-sm">
          Halaman ini dibuka dari tombol "Unduh Laporan PDF" di laporan Analisis Mendalam. Tiket cetaknya sudah
          tidak ada — kembali ke laporan dan klik tombol itu lagi.
        </p>
        <button onClick={() => window.close()} className="mt-2 rounded-xl border border-slate-300 px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100">Tutup tab</button>
      </div>
    );
  }

  if (!cssReady) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <PrintReadyProvider onReady={onReady}>
      <div className="print-root">
        <div className="print-toolbar no-print">
          <div className="grow">
            {ready
              ? <>Siap dicetak. Pilih <b>Simpan sebagai PDF</b> di dialog cetak — nama file: <b>{ticket.fileName}.pdf</b></>
              : <span className="inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Menyiapkan peta dan halaman…</span>}
          </div>
          <button onClick={print} disabled={!ready} className="inline-flex items-center gap-2"><Printer className="w-3.5 h-3.5" /> Cetak / Simpan PDF</button>
          <button onClick={() => window.close()} className="secondary inline-flex items-center gap-2"><X className="w-3.5 h-3.5" /> Tutup</button>
        </div>

        <div className="print-sheet">
          {isPotensi ? <PotensiCover view={view} /> : <PrintCover view={view} />}
          <div className="print-body">
            {isPotensi ? (
              ticket.type === "potensi_iklan"
                ? <IklanReport report={ticket.report} printMode audience={ticket.audience || "internal"} vertical={ticket.vertical || null} />
                : <BisnisReport report={ticket.report} printMode />
            ) : (
              <DeepAnalysisReport
                result={ticket.result}
                targetSes={view.targetSes}
                radius={view.radius}
                profileView={ticket.profileView}
                showBonus={ticket.showBonus ?? true}
                printMode
              />
            )}
          </div>
        </div>
      </div>
    </PrintReadyProvider>
  );
}
